import { ocorrencias } from './lacunas';
import {
  marcarMaisEscapado,
  parearSublinhado,
  protegerMaisEscapado,
  restaurarMaisEscapado,
  semMaisLiteral,
} from './sublinhado';

// Plugin remark da folha de leitura (react-markdown 6): mostra o
// `++sublinhado++` como sublinhado e destaca lacunas e notas de uso. O
// markdown que entra no ReactMarkdown passa antes por `prepararParaLeitura`,
// que protege o `\+` escapado (mesma regra do editor e do agente).

interface No {
  type: string;
  value?: string;
  children?: No[];
  data?: Record<string, unknown>;
}

export const prepararParaLeitura = (markdown: string): string => protegerMaisEscapado(markdown);

const elemento = (type: string, tag: string, children: No[], classe?: string): No => ({
  type,
  data: { hName: tag, ...(classe ? { hProperties: { className: [classe] } } : {}) },
  children,
});

// Um pai com texto direto: os `++` pareados viram um nó sublinhado com os
// irmãos entre eles (o par pode cercar negrito, itálico e link)
const sublinhar = (filhos: No[]): No[] => {
  if (!filhos.some((filho) => filho.type === 'text' && (filho.value || '').includes('++'))) {
    return filhos;
  }
  const trechos = filhos.map((filho) =>
    filho.type === 'text'
      ? { texto: filho.value || '', literal: false }
      : { texto: filho.type === 'break' ? '\n' : 'x', literal: true },
  );
  const pares = parearSublinhado(trechos);
  if (!pares.length) return filhos;

  const delimitador = (posicao: number) =>
    pares.some(
      ([abre, fecha]) =>
        (posicao >= abre && posicao < abre + 2) || (posicao >= fecha && posicao < fecha + 2),
    );

  const saida: No[] = [];
  let grupo: No[] | null = null;
  let posicao = 0;
  const empurrar = (no: No) => (grupo ? grupo.push(no) : saida.push(no));

  filhos.forEach((filho, indice) => {
    if (filho.type !== 'text') {
      empurrar(filho);
      posicao += trechos[indice].texto.length;
      return;
    }
    const valor = filho.value || '';
    let atual = '';
    for (let i = 0; i < valor.length; i += 1) {
      const global = posicao + i;
      if (delimitador(global)) {
        if (atual) empurrar({ type: 'text', value: atual });
        atual = '';
        const ehAbertura = pares.some(([abre]) => global === abre);
        const ehFechamento = pares.some(([, fecha]) => global === fecha);
        if (ehAbertura) grupo = [];
        if (ehFechamento && grupo) {
          saida.push(elemento('sublinhado', 'u', grupo));
          grupo = null;
        }
        i += 1;
      } else {
        atual += valor[i];
      }
    }
    if (atual) empurrar({ type: 'text', value: atual });
    posicao += valor.length;
  });
  if (grupo) saida.push(...(grupo as No[]));
  return saida;
};

// Texto com lacuna ou nota de uso: o trecho vira <mark> com a classe certa
const destacar = (filhos: No[]): No[] => {
  const saida: No[] = [];
  filhos.forEach((filho) => {
    if (filho.type !== 'text') {
      saida.push(filho);
      return;
    }
    const valor = filho.value || '';
    const achadas = ocorrencias(valor);
    if (!achadas.length) {
      saida.push(filho);
      return;
    }
    let cursor = 0;
    achadas.forEach((achada) => {
      if (achada.inicio < cursor) return;
      if (achada.inicio > cursor)
        saida.push({ type: 'text', value: valor.slice(cursor, achada.inicio) });
      saida.push(
        elemento(
          achada.tipo === 'lacuna' ? 'lacuna' : 'notaDeUso',
          'mark',
          [{ type: 'text', value: valor.slice(achada.inicio, achada.fim) }],
          achada.tipo === 'lacuna' ? 'lacuna' : 'nota-de-uso',
        ),
      );
      cursor = achada.fim;
    });
    if (cursor < valor.length) saida.push({ type: 'text', value: valor.slice(cursor) });
  });
  return saida;
};

const transformar = (no: No): void => {
  if (no.type === 'inlineCode' || no.type === 'code') {
    no.value = restaurarMaisEscapado(no.value || '');
    return;
  }
  if (no.type === 'text') {
    no.value = marcarMaisEscapado(no.value || '');
    return;
  }
  if (!no.children) return;
  no.children.forEach(transformar);
  no.children = sublinhar(no.children);
};

// Lacuna escrita como código (`[inserir: …]`, comum nas teses): o código
// inteiro ganha o destaque
const destacarCodigo = (filhos: No[]): No[] =>
  filhos.map((filho) => {
    if (filho.type !== 'inlineCode') return filho;
    const achada = ocorrencias(filho.value || '')[0];
    if (!achada) return filho;
    const lacuna = achada.tipo === 'lacuna';
    return elemento(
      lacuna ? 'lacuna' : 'notaDeUso',
      'mark',
      [filho],
      lacuna ? 'lacuna' : 'nota-de-uso',
    );
  });

const finalizar = (no: No): void => {
  if (no.type === 'text') {
    no.value = semMaisLiteral(no.value || '');
    return;
  }
  if (!no.children) return;
  no.children.forEach(finalizar);
  if (no.type !== 'lacuna' && no.type !== 'notaDeUso') {
    no.children = destacarCodigo(destacar(no.children));
  }
};

const remarkPeticao = () => (arvore: unknown) => {
  transformar(arvore as No);
  finalizar(arvore as No);
};

export default remarkPeticao;
